import { Null, Optional, String, Object, Boolean, Number, Static, Array, Literal, Union } from '@sinclair/typebox';

import { ValueOf } from '../../utils';

import { CodeName, ScheduledDateTime, SplitDuration } from './common';

export const ReservationQueryParams = Object({ pnr: String(), lastName: String() });
export type ReservationQueryParams = Static<typeof ReservationQueryParams>;

const passengerGender = Union([Literal('MALE'), Literal('FEMALE'), Null()]);

export const ReservationPassenger = Object({
  id: Optional(String()),
  passengerKey: String(),
  firstName: String(),
  lastName: String(),
  dateOfBirth: String(),
  gender: passengerGender,
  type: Union([Literal('ADT'), Literal('YTH'), Literal('CHD'), Literal('INF'), Literal('unknown')]),
  isPrimary: Boolean(),
  isRetrievedPassenger: Boolean(),
  isTicketed: Boolean(),
  hasInfant: Boolean(),
  contactInformation: Object({
    email: Optional(String()),
    mobile: Optional(String()),
  }),
});
export type ReservationPassenger = Static<typeof ReservationPassenger>;

export const ReservationConnectionLocation = Object({
  airportCode: String(),
  cityName: String(),
  terminal: Optional(String()),
  countryCode: String(),
  country: String(),
});
export type ReservationConnectionLocation = Static<typeof ReservationConnectionLocation>;

export const ReservationSegment = Object({
  segmentKey: String(),
  id: Number(),
  aircraft: CodeName,
  departure: ReservationConnectionLocation,
  arrival: ReservationConnectionLocation,
  duration: Optional(SplitDuration),
  marketingCarrier: Object({ code: String(), flightNumber: String(), name: String() }),
  operatingCarrier: CodeName,
  scheduledArrival: ScheduledDateTime,
  scheduledDeparture: ScheduledDateTime,
  transitTime: Optional(String()),
  status: String(), // "Active" | "Confirmed" | "Cancelled" | "Flown";
  bookingClass: String(),
  serviceClass: String(),
  flightAcceptanceStatus: String(),
  durationInMinutes: Number(),
});
export type ReservationSegment = Static<typeof ReservationSegment>;

export const ReservationSelfReaccommodation = Object({
  alternativeOfferBoundId: String(),
  isAcknowledgable: Boolean(),
  isRebookable: Boolean(),
  isDisrupted: Boolean(),
  originalDepartureDateTime: String(),
  validSearchRange: Optional(
    Object({
      startDateTime: String(),
      endDateTime: String(),
    })
  ),
  isUN: Boolean(), // If a segment in the bound has the UN status, it means that there is no new bound booked
});
export type ReservationSelfReaccommodation = Static<typeof ReservationSelfReaccommodation>;

export const ReservationConnection = Object({
  conKey: String(),
  isOutbound: Boolean(),
  segments: Array(ReservationSegment),
  departure: ReservationConnectionLocation,
  arrival: ReservationConnectionLocation,
  scheduledArrival: ScheduledDateTime,
  scheduledDeparture: ScheduledDateTime,
  duration: SplitDuration,
  selfReaccommodation: Optional(ReservationSelfReaccommodation),
});

export type ReservationConnection = Static<typeof ReservationConnection>;

const AncillaryType = Union([
  Literal('BAG'),
  Literal('PET'),
  Literal('CLIMATE_COMPENSATION'),
  Literal('FAST_TRACK'),
  Literal('SEAT'),
  Literal('INTERNET'),
  Literal('MEAL'),
  Literal('INSURANCE'),
  Literal('LOUNGE'),
  Literal('SPECIAL_SERVICE', Literal('UNKNOWN')),
  String(),
]);

export const ReservationAncillary = Object({
  id: Optional(String()),
  type: Optional(AncillaryType),
  segmentIds: Optional(Array(String())),
  passengerId: Optional(Array(String())),
  quantity: Optional(Number()),
});
export type ReservationAncillary = Static<typeof ReservationAncillary>;

export const ReservationResponse = Object({
  passengers: Array(ReservationPassenger),
  connections: Array(ReservationConnection),
  ancillaries: Array(ReservationAncillary),
  reservationId: Optional(String()),
  offerId: Optional(String()),
  pos: String(),
});

export type ReservationResponse = Static<typeof ReservationResponse>;

export type ReservationErrorCode = ValueOf<typeof ReservationErrorCode>;
export const ReservationErrorCode = {
  OFFER_ITEMS_REMOVED: 'OFFER_ITEMS_REMOVED',
  OFFER_ITEMS_PRICE_CHANGED: 'OFFER_ITEMS_PRICE_CHANGED',
  FINAL_PRICE_DIFF_FROM_ORDER_CART: 'FINAL_PRICE_DIFF_FROM_ORDER_CART',
} as const;
